// Step 2 — Target (2c product / 2d brand / 2e promotional / 2f category).
//
// One component keyed by wizard `path`. Renders the path radio at the
// top (so operators can pivot without resetting) and then the picker
// appropriate to the chosen path.
//
// Persistence: every checkbox toggle immediately calls
// POST /api/media/:id/assign or DELETE /api/media/:id/assign (Phase 1
// API). This is deliberate — the spec's W1 watchout says back-nav
// from Step 3 must preserve attachments; the way to guarantee that
// is to persist as-you-click, not on Next. Canceling the wizard keeps
// what's already saved (feature, not bug).

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, VStack, HStack, Text, Input, Checkbox, Image, Badge,
  Spinner, useToast, Grid, GridItem, Select
} from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';
import { useBrand } from '../../brand/BrandContext';
import type { MediaAssignments, WizardPath, WizardSelections } from './types';

type Props = {
  value:                 WizardSelections;
  onChange:              (patch: Partial<WizardSelections>) => void;
  // Primary UGC's assignments — drives detect-only muting, branding /
  // promotional confirm-checkbox state, and single-UGC pre-check.
  assignments:           MediaAssignments | null;
  // Phase 7 batch — full map so pickers can compute the intersection
  // of operator attachments across ALL selected UGCs (only fully-
  // attached products pre-check) and dispatch attach/detach to every
  // UGC in one toggle.
  assignmentsByMedia:    Map<string, MediaAssignments>;
  onReloadAssignments:   () => void;
};

export function Step2Target({ value, onChange, assignments, assignmentsByMedia, onReloadAssignments }: Props) {
  const paths: Array<{ key: WizardPath; label: string; helper: string }> = [
    { key: 'product',     label: 'Product',     helper: 'Attach to specific products' },
    { key: 'category',    label: 'Category',    helper: 'Attach to a category (collection campaigns)' },
    { key: 'brand',       label: 'Brand',       helper: 'Use as brand identity signal' },
    { key: 'promotional', label: 'Promotional', helper: 'Promo ad with optional callouts' }
  ];

  return (
    <VStack align="stretch" spacing={5}>
      <Box>
        <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
          Attachment path
        </Text>
        <HStack spacing={2} flexWrap="wrap">
          {paths.map(p => {
            const active = value.path === p.key;
            return (
              <Box
                key={p.key}
                as="button"
                type="button"
                onClick={() => onChange({ path: p.key })}
                px={3}
                py={2}
                borderWidth="1px"
                borderColor={active ? 'rsViolet.500' : 'brand.border'}
                bg={active ? 'rsViolet.50' : 'white'}
                color={active ? 'rsViolet.700' : 'brand.ink'}
                borderRadius="md"
                fontSize="sm"
                fontWeight="700"
                cursor="pointer"
                textAlign="left"
                minW="200px"
              >
                <Text fontWeight="800">{p.label}</Text>
                <Text fontSize="11px" color="brand.muted" mt={0.5}>{p.helper}</Text>
              </Box>
            );
          })}
        </HStack>
      </Box>

      {value.path === 'product'     && <ProductPicker    value={value} onChange={onChange} assignments={assignments} assignmentsByMedia={assignmentsByMedia} onReloadAssignments={onReloadAssignments} />}
      {value.path === 'category'    && <CategoryPicker   value={value} onChange={onChange} assignments={assignments} assignmentsByMedia={assignmentsByMedia} onReloadAssignments={onReloadAssignments} />}
      {value.path === 'brand'       && <BrandConfirm     value={value} assignmentsByMedia={assignmentsByMedia} onReloadAssignments={onReloadAssignments} />}
      {value.path === 'promotional' && <PromotionalPicker value={value} onChange={onChange} assignments={assignments} assignmentsByMedia={assignmentsByMedia} onReloadAssignments={onReloadAssignments} />}
    </VStack>
  );
}

// ── Product path (2c) ────────────────────────────────────────────────
// Catalog multi-select. Existing operator-attached products pre-check
// on load (assignments.products where source==='operator'). Detect-
// derived attachments render muted so the distinction stays visible.

// Response shape from GET /api/catalog list — projectListRow emits
// `id: String(p._id)`, NOT productId. That naming diverges from
// /api/catalog/ads-summary (which does emit productId) — the mismatch
// silently broke the picker's attach flow (r.productId = undefined →
// 400 targetId required) until fixed.
type CatalogRow = {
  id:         string;
  title:      string;
  imageUrl:   string | null;
  price:      number | null;
  currency:   string | null;
};

function ProductPicker({
  value, onChange, assignments, assignmentsByMedia, onReloadAssignments
}: {
  value:               WizardSelections;
  onChange:            (patch: Partial<WizardSelections>) => void;
  assignments:         MediaAssignments | null;
  assignmentsByMedia:  Map<string, MediaAssignments>;
  onReloadAssignments: () => void;
}) {
  const { activeBrand } = useBrand();
  const toast = useToast();
  const [rows, setRows]       = useState<CatalogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [q, setQ]             = useState('');
  const [busy, setBusy]       = useState<Set<string>>(new Set());
  const seededRef             = useRef(false);

  // Phase 7 — compute the INTERSECTION of operator-attached products
  // across every selected UGC. Only products where EVERY selected UGC
  // has an operator attachment pre-check; a partially-attached product
  // renders as an unchecked-but-known state (badge below) so the
  // operator can see what's already attached to some of the batch.
  const isBatch = value.mediaIds.length > 1;
  const { intersectionIds, partialIds } = useMemo(() => {
    if (value.mediaIds.length === 0) return { intersectionIds: new Set<string>(), partialIds: new Set<string>() };
    const perMediaSets = value.mediaIds.map(id => {
      const a = assignmentsByMedia.get(id);
      return new Set(
        (a?.products || [])
          .filter(p => p.source === 'operator' && p.catalogProductId)
          .map(p => p.catalogProductId as string)
      );
    });
    if (perMediaSets.length === 0) return { intersectionIds: new Set<string>(), partialIds: new Set<string>() };
    // Intersection: id must be in every set.
    let inter = new Set(perMediaSets[0]);
    for (let i = 1; i < perMediaSets.length; i++) {
      inter = new Set([...inter].filter(id => perMediaSets[i].has(id)));
    }
    // Partial: id in at least one set but not all.
    const union = new Set<string>();
    for (const s of perMediaSets) for (const id of s) union.add(id);
    const partial = new Set([...union].filter(id => !inter.has(id)));
    return { intersectionIds: inter, partialIds: partial };
  }, [assignmentsByMedia, value.mediaIds]);

  // Pre-check the intersection (products attached to ALL selected UGCs).
  // Runs once when assignments first arrive so the operator can uncheck
  // without a rehydration battle.
  useEffect(() => {
    if (seededRef.current || assignmentsByMedia.size === 0) return;
    const preChecked = [...intersectionIds];
    if (preChecked.length > 0 && value.productIds.length === 0) {
      onChange({ productIds: preChecked });
    }
    seededRef.current = true;
  }, [assignmentsByMedia, intersectionIds, value.productIds.length, onChange]);

  useEffect(() => {
    if (!activeBrand?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const qp = new URLSearchParams({ brandId: activeBrand.id, limit: '100' });
        if (q.trim()) qp.set('q', q.trim());
        const res = await apiJson<{ products: CatalogRow[] }>(`/api/catalog?${qp.toString()}`);
        if (cancelled) return;
        setRows(res.products || []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load catalog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBrand?.id, q]);

  const detectOnlyIds = useMemo(() => {
    if (!assignments) return new Set<string>();
    const ops = new Set(
      assignments.products
        .filter(p => p.source === 'operator' && p.catalogProductId)
        .map(p => p.catalogProductId as string)
    );
    return new Set(
      assignments.products
        .filter(p => p.source === 'detect' && p.catalogProductId && !ops.has(p.catalogProductId as string))
        .map(p => p.catalogProductId as string)
    );
  }, [assignments]);

  // Fan-out toggle — one HTTP per selected UGC. Sequential rather than
  // parallel because the backend's tenant validation on attachProduct
  // is fast enough that serialisation buys nothing (~50ms/req × N), and
  // sequential is easier to reason about on partial failure (skip the
  // rest, restore the button, show a clear message).
  const toggle = async (productId: string, checked: boolean) => {
    if (value.mediaIds.length === 0) return;
    setBusy(prev => new Set(prev).add(productId));
    const method = checked ? 'POST' : 'DELETE';
    const failures: string[] = [];
    try {
      for (const mediaId of value.mediaIds) {
        try {
          await apiJson(`/api/media/${mediaId}/assign`, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ targetType: 'product', targetId: productId })
          });
        } catch (err) {
          failures.push(`${mediaId.slice(-6)}: ${err instanceof Error ? err.message : 'unknown'}`);
        }
      }
      if (failures.length === value.mediaIds.length) {
        // All failed — do not update UI state.
        throw new Error(failures[0] || 'all attaches failed');
      }
      // At least one succeeded — update UI. Partial failure warns.
      if (checked) {
        onChange({ productIds: [...value.productIds, productId] });
      } else {
        onChange({ productIds: value.productIds.filter(id => id !== productId) });
      }
      if (failures.length > 0) {
        toast({
          title: `Partial ${checked ? 'attach' : 'detach'}`,
          description: `${failures.length}/${value.mediaIds.length} UGCs failed. ${failures[0]}`,
          status: 'warning',
          duration: 5000
        });
      }
      onReloadAssignments();
    } catch (err) {
      toast({
        title: checked ? 'Attach failed' : 'Detach failed',
        description: err instanceof Error ? err.message : 'unknown error',
        status: 'error',
        duration: 5000
      });
    } finally {
      setBusy(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  return (
    <Box>
      <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
        Attach to products
      </Text>
      <Input
        size="sm"
        placeholder="Search catalog by title or description"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        maxW="480px"
        bg="white"
        mb={3}
      />
      {loading && rows.length === 0 && (
        <HStack py={4}><Spinner size="sm" /><Text fontSize="sm" color="brand.muted">Loading catalog…</Text></HStack>
      )}
      {error && <Text fontSize="sm" color="red.600">{error}</Text>}
      {rows.length > 0 && (
        <Grid templateColumns="repeat(auto-fill, minmax(220px, 1fr))" gap={2} maxH="440px" overflowY="auto">
          {rows.map(r => {
            const checked   = value.productIds.includes(r.id);
            const detectOnly = detectOnlyIds.has(r.id);
            const isBusy    = busy.has(r.id);
            return (
              <GridItem
                key={r.id}
                borderWidth="1px"
                borderColor={checked ? 'rsViolet.500' : 'brand.border'}
                borderRadius="md"
                p={2}
                bg={checked ? 'rsViolet.50' : 'white'}
                opacity={detectOnly && !checked ? 0.65 : 1}
              >
                <HStack align="flex-start" spacing={2}>
                  <Checkbox
                    isChecked={checked}
                    isDisabled={isBusy}
                    onChange={(e) => toggle(r.id, e.target.checked)}
                    mt={1}
                  />
                  {r.imageUrl && (
                    <Image src={r.imageUrl} alt="" boxSize="40px" objectFit="cover" borderRadius="sm" />
                  )}
                  <Box minW={0} flex={1}>
                    <Text fontSize="xs" fontWeight="700" noOfLines={2} color="brand.ink">
                      {r.title}
                    </Text>
                    <HStack spacing={1} mt={0.5} flexWrap="wrap">
                      {r.price != null && (
                        <Text fontSize="10px" color="brand.muted">
                          {r.currency || '$'}{r.price}
                        </Text>
                      )}
                      {detectOnly && !checked && (
                        <Badge fontSize="9px" colorScheme="gray" variant="subtle">detected</Badge>
                      )}
                      {/* Phase 7 batch — product attached to SOME but not
                          ALL of the selected UGCs. Ticking will attach the
                          rest so the intersection extends. */}
                      {isBatch && !checked && partialIds.has(r.id) && (
                        <Badge fontSize="9px" colorScheme="yellow" variant="subtle" title="Attached to some UGCs in the batch">partial</Badge>
                      )}
                    </HStack>
                  </Box>
                </HStack>
              </GridItem>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}

// ── Category path (2f) ───────────────────────────────────────────────
// Cascading picker — GET /api/catalog/categories returns one level per
// request (root when parentId is omitted, direct children when parentId
// is an id) plus hasChildren. Same load/drill state machine as Product
// Ads (pages/ProductAds/index.tsx). includeEmpty=true so a brand-new
// category with no products is still assignable, which is the case this
// picker exists to serve. Multi-select is independent across levels:
// checking a parent does not check its children (assignment is per
// categoryId; collection campaigns can target a parent without implying
// every leaf).

type CategoryRow = {
  categoryId:   string;
  name:         string;
  breadcrumb:   string;
  depth:        number;
  productCount: number;
  hasChildren:  boolean;
};

function CategoryPicker({
  value, onChange, assignments: _assignments, assignmentsByMedia, onReloadAssignments
}: {
  value:               WizardSelections;
  onChange:            (patch: Partial<WizardSelections>) => void;
  assignments:         MediaAssignments | null;
  assignmentsByMedia:  Map<string, MediaAssignments>;
  onReloadAssignments: () => void;
}) {
  const { activeBrand } = useBrand();
  const toast = useToast();
  const [categoryLevels, setCategoryLevels] = useState<CategoryRow[][]>([]);
  const [categoryPath, setCategoryPath]     = useState<string[]>([]);
  const categoryPathRef = useRef<string[]>([]);
  categoryPathRef.current = categoryPath;
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [q, setQ]             = useState('');
  const [busy, setBusy]       = useState<Set<string>>(new Set());
  const seededRef             = useRef(false);

  // Intersection of operator-attached categories across selected UGCs
  // (same shape as ProductPicker's intersection logic).
  const intersectionIds = useMemo(() => {
    if (value.mediaIds.length === 0) return new Set<string>();
    const perMediaSets = value.mediaIds.map(id => {
      const a = assignmentsByMedia.get(id);
      return new Set(
        (a?.categories || [])
          .filter(c => c.source === 'operator' && c.categoryId)
          .map(c => c.categoryId as string)
      );
    });
    let inter = new Set(perMediaSets[0] || []);
    for (let i = 1; i < perMediaSets.length; i++) {
      inter = new Set([...inter].filter(id => perMediaSets[i].has(id)));
    }
    return inter;
  }, [assignmentsByMedia, value.mediaIds]);

  useEffect(() => {
    if (seededRef.current || assignmentsByMedia.size === 0) return;
    const preChecked = [...intersectionIds];
    if (preChecked.length > 0 && value.categoryIds.length === 0) {
      onChange({ categoryIds: preChecked });
    }
    seededRef.current = true;
  }, [assignmentsByMedia, intersectionIds, value.categoryIds.length, onChange]);

  useEffect(() => {
    if (!activeBrand?.id) {
      setCategoryLevels([]);
      setCategoryPath([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await apiJson<CategoryRow[]>(
          `/api/catalog/categories?brandId=${encodeURIComponent(activeBrand.id)}&includeEmpty=true`
        );
        if (cancelled) return;
        setCategoryLevels([res || []]);
        setCategoryPath([]);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load categories');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBrand?.id]);

  async function selectCategoryAt(levelIdx: number, categoryId: string) {
    if (!activeBrand?.id) return;
    if (!categoryId) {
      setCategoryPath(prev => prev.slice(0, levelIdx));
      setCategoryLevels(prev => prev.slice(0, levelIdx + 1));
      return;
    }
    const nextPath = [...categoryPath.slice(0, levelIdx), categoryId];
    setCategoryPath(nextPath);
    setCategoryLevels(prev => prev.slice(0, levelIdx + 1));

    const chosen = (categoryLevels[levelIdx] || []).find(r => r.categoryId === categoryId);
    if (!chosen || !chosen.hasChildren) return;

    const url = `/api/catalog/categories?brandId=${encodeURIComponent(activeBrand.id)}&parentId=${encodeURIComponent(categoryId)}&includeEmpty=true`;
    try {
      const children = await apiJson<CategoryRow[]>(url);
      if (categoryPathRef.current[levelIdx] !== categoryId) return;
      setCategoryLevels(prev => {
        if (prev.length <= levelIdx) return prev;
        const next = prev.slice(0, levelIdx + 1);
        next.push(children || []);
        return next;
      });
    } catch {
      // Silent — the parent stays selected as the drill target; attach
      // still uses the checkbox on that parent.
    }
  }

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of assignmentsByMedia.values()) {
      for (const c of a.categories || []) {
        if (c.categoryId) m.set(c.categoryId, c.name || c.breadcrumbKey || c.categoryId);
      }
    }
    for (const level of categoryLevels) {
      for (const r of level) m.set(r.categoryId, r.breadcrumb || r.name);
    }
    return m;
  }, [assignmentsByMedia, categoryLevels]);

  const currentRows = categoryLevels[categoryPath.length] || categoryLevels[categoryLevels.length - 1] || [];
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return currentRows;
    return currentRows.filter(r =>
      r.name.toLowerCase().includes(term) ||
      (r.breadcrumb || '').toLowerCase().includes(term)
    );
  }, [currentRows, q]);

  const toggle = async (categoryId: string, checked: boolean) => {
    if (value.mediaIds.length === 0) return;
    setBusy(prev => new Set(prev).add(categoryId));
    const method = checked ? 'POST' : 'DELETE';
    const failures: string[] = [];
    try {
      for (const mediaId of value.mediaIds) {
        try {
          await apiJson(`/api/media/${mediaId}/assign`, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ targetType: 'category', targetId: categoryId })
          });
        } catch (err) {
          failures.push(err instanceof Error ? err.message : 'unknown');
        }
      }
      if (failures.length === value.mediaIds.length) throw new Error(failures[0]);
      if (checked) {
        onChange({ categoryIds: [...value.categoryIds, categoryId] });
      } else {
        onChange({ categoryIds: value.categoryIds.filter(id => id !== categoryId) });
      }
      if (failures.length > 0) {
        toast({
          title: `Partial ${checked ? 'attach' : 'detach'}`,
          description: `${failures.length}/${value.mediaIds.length} UGCs failed`,
          status: 'warning'
        });
      }
      onReloadAssignments();
    } catch (err) {
      toast({
        title: checked ? 'Attach failed' : 'Detach failed',
        description: err instanceof Error ? err.message : 'unknown error',
        status: 'error'
      });
    } finally {
      setBusy(prev => {
        const next = new Set(prev);
        next.delete(categoryId);
        return next;
      });
    }
  };

  return (
    <Box>
      <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
        Attach to categories
      </Text>
      <Text fontSize="11px" color="brand.muted" mb={2}>
        Drill with the dropdowns to reach leaf categories. Checking a parent does not check its children.
      </Text>
      {categoryLevels[0] && categoryLevels[0].length > 0 && (
        <HStack spacing={3} align="center" flexWrap="wrap" mb={3}>
          {categoryLevels.map((rows, levelIdx) => {
            if (!rows || rows.length === 0) return null;
            const selectValue = categoryPath[levelIdx] || '';
            const allLabel = levelIdx === 0
              ? `All categories (${rows.reduce((s, c) => s + c.productCount, 0)})`
              : 'All';
            return (
              <Select
                key={levelIdx}
                size="sm"
                maxW="260px"
                value={selectValue}
                onChange={(e) => void selectCategoryAt(levelIdx, e.target.value)}
              >
                <option value="">{allLabel}</option>
                {rows.map(c => (
                  <option key={c.categoryId} value={c.categoryId}>
                    {c.name} ({c.productCount})
                  </option>
                ))}
              </Select>
            );
          })}
        </HStack>
      )}
      {value.categoryIds.length > 0 && (
        <HStack spacing={2} flexWrap="wrap" mb={3}>
          {value.categoryIds.map(id => (
            <Badge key={id} fontSize="10px" colorScheme="purple" variant="subtle">
              {nameById.get(id) || id}
            </Badge>
          ))}
        </HStack>
      )}
      <Input
        size="sm"
        placeholder="Filter categories at this level"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        maxW="480px"
        bg="white"
        mb={3}
      />
      {loading && <HStack py={4}><Spinner size="sm" /><Text fontSize="sm" color="brand.muted">Loading…</Text></HStack>}
      {error && <Text fontSize="sm" color="red.600">{error}</Text>}
      {filtered.length > 0 && (
        <VStack align="stretch" spacing={1} maxH="440px" overflowY="auto">
          {filtered.map(r => {
            const checked = value.categoryIds.includes(r.categoryId);
            const isBusy = busy.has(r.categoryId);
            return (
              <HStack
                key={r.categoryId}
                borderWidth="1px"
                borderColor={checked ? 'rsViolet.500' : 'brand.border'}
                bg={checked ? 'rsViolet.50' : 'white'}
                borderRadius="md"
                px={3}
                py={2}
                spacing={3}
              >
                <Checkbox
                  isChecked={checked}
                  isDisabled={isBusy}
                  onChange={(e) => toggle(r.categoryId, e.target.checked)}
                />
                <Box flex={1} minW={0}>
                  <Text fontSize="sm" fontWeight="700" color="brand.ink" noOfLines={1}>
                    {r.name}
                  </Text>
                  {r.breadcrumb && (
                    <Text fontSize="10px" color="brand.muted" noOfLines={1}>{r.breadcrumb}</Text>
                  )}
                  {r.hasChildren && (
                    <Text fontSize="10px" color="brand.muted">has subcategories</Text>
                  )}
                </Box>
                <Badge fontSize="9px" colorScheme="gray" variant="subtle">
                  {r.productCount} product{r.productCount === 1 ? '' : 's'}
                </Badge>
              </HStack>
            );
          })}
        </VStack>
      )}
    </Box>
  );
}

// ── Brand path (2d) ──────────────────────────────────────────────────
// No picker — a single confirmation. The wizard's Step 3 filters to
// Campaign.kind='brand' campaigns; this Step 2 just persists the
// branding assignment on toggle.

function BrandConfirm({
  value, assignmentsByMedia, onReloadAssignments
}: {
  value:               WizardSelections;
  assignmentsByMedia:  Map<string, MediaAssignments>;
  onReloadAssignments: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // Phase 7 — batch attach state: attached only when EVERY selected UGC
  // has the branding assignment; partial highlights the split.
  const attachedCount = value.mediaIds.filter(id => !!assignmentsByMedia.get(id)?.branding).length;
  const attached = attachedCount === value.mediaIds.length && value.mediaIds.length > 0;
  const partial  = attachedCount > 0 && attachedCount < value.mediaIds.length;

  const toggle = async (checked: boolean) => {
    if (value.mediaIds.length === 0) return;
    setBusy(true);
    const method = checked ? 'POST' : 'DELETE';
    const failures: string[] = [];
    try {
      for (const mediaId of value.mediaIds) {
        try {
          await apiJson(`/api/media/${mediaId}/assign`, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ targetType: 'branding' })
          });
        } catch (err) {
          failures.push(err instanceof Error ? err.message : 'unknown');
        }
      }
      if (failures.length === value.mediaIds.length) throw new Error(failures[0]);
      if (failures.length > 0) {
        toast({
          title: `Partial ${checked ? 'attach' : 'detach'}`,
          description: `${failures.length}/${value.mediaIds.length} UGCs failed`,
          status: 'warning'
        });
      }
      onReloadAssignments();
    } catch (err) {
      toast({
        title: checked ? 'Attach failed' : 'Detach failed',
        description: err instanceof Error ? err.message : 'unknown error',
        status: 'error'
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="white" p={4}>
      <Checkbox
        size="lg"
        isChecked={attached}
        isIndeterminate={partial}
        isDisabled={busy}
        onChange={(e) => toggle(e.target.checked)}
      >
        <Text fontSize="sm" fontWeight="700" color="brand.ink">
          Attach {value.mediaIds.length > 1 ? `${value.mediaIds.length} UGCs` : 'this UGC'} as brand identity signal
          {partial && <Text as="span" fontSize="xs" color="orange.700" ml={2}>({attachedCount}/{value.mediaIds.length} attached)</Text>}
        </Text>
      </Checkbox>
      <Text fontSize="xs" color="brand.muted" mt={2} ml={7}>
        Brand attachments feed the storyboard's brand voice — no product callouts required.
        Step 3 will filter to brand-kind campaigns.
      </Text>
    </Box>
  );
}

// ── Promotional path (2e) ────────────────────────────────────────────
// Two toggles: (1) attach as promotional (persists the promotional
// assignment); (2) optional product callouts (persists a productIds
// list under the promotional assignment).

function PromotionalPicker({
  value, onChange, assignments, assignmentsByMedia, onReloadAssignments
}: {
  value:               WizardSelections;
  onChange:            (patch: Partial<WizardSelections>) => void;
  assignments:         MediaAssignments | null;
  assignmentsByMedia:  Map<string, MediaAssignments>;
  onReloadAssignments: () => void;
}) {
  const { activeBrand } = useBrand();
  const toast = useToast();
  const [rows, setRows]           = useState<CatalogRow[]>([]);
  const [showCallouts, setShow]   = useState(false);
  const [q, setQ]                 = useState('');
  const [busy, setBusy]           = useState(false);
  // Attached only when ALL selected UGCs have promotional assignment.
  const attachedCount = value.mediaIds.filter(id => !!assignmentsByMedia.get(id)?.promotional).length;
  const attached      = attachedCount === value.mediaIds.length && value.mediaIds.length > 0;
  const partial       = attachedCount > 0 && attachedCount < value.mediaIds.length;

  useEffect(() => {
    if (!attached && value.calloutProductIds.length === 0) setShow(false);
  }, [attached, value.calloutProductIds.length]);

  useEffect(() => {
    if (!showCallouts || !activeBrand?.id) return;
    const qp = new URLSearchParams({ brandId: activeBrand.id, limit: '50' });
    if (q.trim()) qp.set('q', q.trim());
    void apiJson<{ products: CatalogRow[] }>(`/api/catalog?${qp.toString()}`)
      .then(res => setRows(res.products || []))
      .catch(() => setRows([]));
  }, [showCallouts, activeBrand?.id, q]);

  // Seed callouts from persisted promotional.productIds on first load.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !assignments?.promotional) return;
    if (assignments.promotional.productIds.length > 0 && value.calloutProductIds.length === 0) {
      onChange({ calloutProductIds: assignments.promotional.productIds });
      setShow(true);
    }
    seededRef.current = true;
  }, [assignments, value.calloutProductIds.length, onChange]);

  // Batch: same promotional (with the same callouts) applied to every
  // selected UGC. Different callouts per UGC would need a per-UGC UI
  // that Phase 7's MVP doesn't ship — this shared model is what the
  // preview at Step 5 assumes too.
  const savePromotional = async (checked: boolean, productIds: string[]) => {
    if (value.mediaIds.length === 0) return;
    setBusy(true);
    const failures: string[] = [];
    try {
      for (const mediaId of value.mediaIds) {
        try {
          if (checked) {
            await apiJson(`/api/media/${mediaId}/assign`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ targetType: 'promotional', productIds })
            });
          } else {
            await apiJson(`/api/media/${mediaId}/assign`, {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ targetType: 'promotional' })
            });
          }
        } catch (err) {
          failures.push(err instanceof Error ? err.message : 'unknown');
        }
      }
      if (failures.length === value.mediaIds.length) throw new Error(failures[0]);
      if (failures.length > 0) {
        toast({
          title: `Partial ${checked ? 'attach' : 'detach'}`,
          description: `${failures.length}/${value.mediaIds.length} UGCs failed`,
          status: 'warning'
        });
      }
      onReloadAssignments();
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'unknown error',
        status: 'error'
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleAttached = async (checked: boolean) => {
    await savePromotional(checked, checked ? value.calloutProductIds : []);
    if (!checked) {
      onChange({ calloutProductIds: [] });
      setShow(false);
    }
  };

  const toggleCallout = async (productId: string, checked: boolean) => {
    const next = checked
      ? [...value.calloutProductIds, productId]
      : value.calloutProductIds.filter(id => id !== productId);
    onChange({ calloutProductIds: next });
    if (attached) await savePromotional(true, next);
  };

  return (
    <VStack align="stretch" spacing={3}>
      <Box borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="white" p={4}>
        <Checkbox
          size="lg"
          isChecked={attached}
          isIndeterminate={partial}
          isDisabled={busy}
          onChange={(e) => toggleAttached(e.target.checked)}
        >
          <Text fontSize="sm" fontWeight="700" color="brand.ink">
            Attach {value.mediaIds.length > 1 ? `${value.mediaIds.length} UGCs` : 'this UGC'} as promotional
            {partial && <Text as="span" fontSize="xs" color="orange.700" ml={2}>({attachedCount}/{value.mediaIds.length} attached)</Text>}
          </Text>
        </Checkbox>
        <Text fontSize="xs" color="brand.muted" mt={2} ml={7}>
          Step 3 will filter to promotional-kind campaigns. Optionally add product callouts below.
          {value.mediaIds.length > 1 && ' Callouts apply to every UGC in the batch.'}
        </Text>
      </Box>

      {attached && (
        <Box borderWidth="1px" borderColor="brand.border" borderRadius="md" bg="brand.surface" p={4}>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
              Product callouts (optional)
            </Text>
            <Text
              as="button"
              type="button"
              onClick={() => setShow(v => !v)}
              fontSize="11px"
              color="rsViolet.600"
              fontWeight="700"
              cursor="pointer"
            >
              {showCallouts ? 'Hide' : 'Add'}
            </Text>
          </HStack>
          {showCallouts && (
            <>
              <Input
                size="sm"
                placeholder="Search products"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                maxW="360px"
                mb={2}
                bg="white"
              />
              <Grid templateColumns="repeat(auto-fill, minmax(220px, 1fr))" gap={2} maxH="280px" overflowY="auto">
                {rows.map(r => {
                  const checked = value.calloutProductIds.includes(r.id);
                  return (
                    <GridItem
                      key={r.id}
                      borderWidth="1px"
                      borderColor={checked ? 'rsViolet.500' : 'brand.border'}
                      bg={checked ? 'rsViolet.50' : 'white'}
                      borderRadius="md"
                      p={2}
                    >
                      <HStack align="flex-start" spacing={2}>
                        <Checkbox
                          isChecked={checked}
                          onChange={(e) => toggleCallout(r.id, e.target.checked)}
                          mt={1}
                        />
                        {r.imageUrl && <Image src={r.imageUrl} alt="" boxSize="36px" objectFit="cover" borderRadius="sm" />}
                        <Text fontSize="xs" fontWeight="700" noOfLines={2} color="brand.ink">
                          {r.title}
                        </Text>
                      </HStack>
                    </GridItem>
                  );
                })}
              </Grid>
            </>
          )}
        </Box>
      )}
    </VStack>
  );
}
